// src/components/ExpenseList.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { isValid, format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { it } from 'date-fns/locale';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  OutlinedInput,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Tooltip,
  Skeleton,
  BottomNavigation,
  BottomNavigationAction,
  TextField,
  Alert
} from '@mui/material';
import Zoom from '@mui/material/Zoom';
import {
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  AddCircle as AddIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  ListAlt as ListAltIcon
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { useTheme } from '@mui/material/styles';
import { styles } from './ExpenseListStyles';
import './ExpenseList.css';
import { auth, db } from '../firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc
} from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import AddExpense from './AddExpense';
// Importa fetchUserFamily per ottenere lo stato aggiornato della famiglia
import { fetchUserFamily } from './DashboardFamily';

const LOCAL_STORAGE_KEY = 'auth_state';

function stringToHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

const safeParseDate = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date && !isNaN(dateInput)) return dateInput;
  if (typeof dateInput.toDate === 'function') return dateInput.toDate();
  try {
    const parsedDate = parseISO(dateInput);
    return isValid(parsedDate) ? parsedDate : null;
  } catch (e) {
    console.warn('Data non valida ignorata:', dateInput);
    return null;
  }
};

const getPallinoClass = (name, type, colorSets) => {
  const typeColors = colorSets[type] || colorSets.category;
  const hashedValue = stringToHash(name || '');
  const index = Math.abs(hashedValue) % typeColors.length;
  return `pallino-${typeColors[index]}`;
};

const validateAmount = (amount) => {
  if (typeof amount !== 'number' && (typeof amount !== 'string' || amount.trim() === ''))
    return false;
  const numAmount = parseFloat(amount);
  return !isNaN(numAmount) && numAmount >= 0;
};

const ExpenseList = () => {
  // Dichiara lo stato e le variabili
  const [expenses, setExpenses] = useState([]);
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  });
  const [userFamily, setUserFamily] = useState(null);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchName, setSearchName] = useState('');
  const [loading, setLoading] = useState(true);
  const [monthlyBudgets, setMonthlyBudgets] = useState({});
  const [budgetInput, setBudgetInput] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [budgetLoading, setBudgetLoading] = useState(true);
  const [editBudgetOpen, setEditBudgetOpen] = useState(false);
  const [bottomNavValue, setBottomNavValue] = useState('expense-list');
  const [isFetchingExpenses, setIsFetchingExpenses] = useState(false);
  const [pendingDeletions, setPendingDeletions] = useState([]);
  const [needsRestoration, setNeedsRestoration] = useState(null);
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);

  const theme = useTheme();
  const modeClass = theme.palette.mode === 'light' ? 'light-mode' : 'dark-mode';

  // Dichiara navigate
  const navigate = useNavigate();
  const location = useLocation();

  const containerSx = {
    ...styles.fadeTransition,
    padding: { xs: 2, md: 4 },
    backgroundColor: 'background.default',
    minHeight: '100vh',
    maxWidth: '1200px',
    margin: '0 auto',
    boxSizing: 'border-box',
  };

  const currentDate = new Date();
  const currentYear = format(currentDate, 'yyyy');
  const currentMonthValue = format(currentDate, 'yyyy-MM');

  const colorSets = useMemo(() => {
    return {
      year: ['year-red', 'year-blue', 'year-green', 'year-orange', 'year-purple', 'year-teal'],
      month: theme.palette.mode === 'light'
        ? ['month-pink', 'month-lavender', 'month-coral', 'month-peach', 'month-mint', 'month-skyblue']
        : ['month-yellow', 'month-pink', 'month-lavender', 'month-coral', 'month-peach', 'month-mint', 'month-skyblue'],
      category: theme.palette.mode === 'light'
        ? ['category-red', 'category-blue', 'category-green', 'category-orange', 'category-purple', 'category-teal', 'category-pink', 'category-lavender', 'category-coral', 'category-skyblue']
        : ['category-red', 'category-blue', 'category-green', 'category-orange', 'category-purple', 'category-teal', 'category-yellow', 'category-pink', 'category-lavender', 'category-coral'],
    };
  }, [theme.palette.mode]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        navigate('/');
        return;
      }
      const userToStore = {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName,
      };
      setUser(userToStore);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(userToStore));
    });
    return () => unsubscribe();
  }, [navigate]);

  const checkAuth = useCallback(() => {
    const savedAuth = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!savedAuth && !auth.currentUser) {
      navigate('/');
      return false;
    }
    return true;
  }, [navigate]);

  // Recupera lo stato aggiornato della famiglia
  useEffect(() => {
    const fetchFamily = async () => {
      await fetchUserFamily(setUserFamily);
    };
    fetchFamily();
  }, []);

  // Listener in tempo reale per lespese
  useEffect(() => {
    let unsubscribeExpenses;
    const handleFirestoreError = (fireErr) => {
      console.error('Errore Firestore:', fireErr);
      setError('Errore nel recupero delle spese.');
      setLoading(false);
      if (fireErr.code === 'permission-denied') {
        checkAuth();
      }
    };

    const setupRealtimeListener = () => {
      if (!checkAuth()) return;
      const userId = user?.uid || auth.currentUser?.uid;
      if (!userId) return;
      let expensesQuery;
      if (userFamily && userFamily.id) {
        expensesQuery = query(
          collection(db, 'expenses'),
          where('familyId', '==', userFamily.id),
          orderBy('date', 'desc')
        );
      } else {
        expensesQuery = query(
          collection(db, 'expenses'),
          where('userId', '==', userId),
          where('familyId', '==', null),
          orderBy('date', 'desc')
        );
      }
      unsubscribeExpenses = onSnapshot(
        expensesQuery,
        (snapshot) => {
          const expensesData = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          setExpenses(expensesData);
          setLoading(false);
        },
        handleFirestoreError
      );
    };

    if (user) {
      setupRealtimeListener();
    }
    return () => {
      if (unsubscribeExpenses) unsubscribeExpenses();
    };
  }, [userFamily, user, checkAuth]);

  // Fetch del budget mensile
  useEffect(() => {
    const fetchMonthlyBudget = async () => {
      if (!checkAuth()) return;
      setBudgetLoading(true);
      try {
        const userId = user?.uid || auth.currentUser?.uid;
        if (!userId) {
          setBudgetLoading(false);
          return;
        }
        const budgetRef = doc(db, 'budgets', `${userId}_${currentMonthValue}`);
        const budgetSnapshot = await getDoc(budgetRef);
        if (budgetSnapshot.exists()) {
          const budgetData = budgetSnapshot.data();
          setMonthlyBudgets((prev) => ({
            ...prev,
            [currentMonthValue]: budgetData.budgetAmount || 0,
          }));
        }
      } catch (err) {
        console.error('Errore nel recupero del budget:', err);
      } finally {
        setBudgetLoading(false);
      }
    };
    if (user) fetchMonthlyBudget();
  }, [currentMonthValue, user, checkAuth]);

  // Estrazione delle categorie dalle spese filtrate
  useEffect(() => {
    const extractCategories = (data) => {
      const uniqueCategories = [...new Set(data.map(expense => expense.category).filter(Boolean))];
      setCategories(uniqueCategories);
    };
    extractCategories(filterExpenses(expenses));
  }, [expenses, selectedCategory, searchName]);

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleSearchNameChange = (event) => {
    setSearchName(event.target.value);
  };

  const filterExpenses = (allExpenses) => {
    return allExpenses.filter(expense => {
      const matchesCategory = selectedCategory ? expense.category === selectedCategory : true;
      const matchesName = expense.name
        ? expense.name.toLowerCase().includes(searchName.toLowerCase())
        : searchName === '';
      return matchesCategory && matchesName;
    });
  };

  const getGroupedExpenses = useCallback(() => {
    const filtered = filterExpenses(expenses);
    return filtered.reduce((acc, expense) => {
      const date = safeParseDate(expense.date);
      if (!date) return acc;
      const year = format(date, 'yyyy');
      const month = format(date, 'MMMM', { locale: it });
      const amount = parseFloat(expense.amount) || 0;
      if (!validateAmount(amount)) {
        console.warn('Importo non valido in spesa:', expense);
        enqueueSnackbar('Importo non valido in una delle spese.', { variant: 'warning' });
        return acc;
      }
      if (!acc[year]) {
        acc[year] = { total: 0, months: {}, totalTransactions: 0 };
      }
      acc[year].total += amount;
      acc[year].totalTransactions += 1;
      if (!acc[year].months[month]) {
        acc[year].months[month] = { total: 0, categories: {}, totalTransactions: 0 };
      }
      acc[year].months[month].total += amount;
      acc[year].months[month].totalTransactions += 1;
      const cat = expense.category || 'Non specificata';
      if (!acc[year].months[month].categories[cat]) {
        acc[year].months[month].categories[cat] = { total: 0, transactions: [] };
      }
      acc[year].months[month].categories[cat].total += amount;
      acc[year].months[month].categories[cat].transactions.push(expense);
      return acc;
    }, {});
  }, [expenses, filterExpenses]);

  const totalSpent = useCallback(() => {
    const currentMonthExpenses = expenses.filter(expense => {
      const expenseDate = safeParseDate(expense.date);
      return expenseDate && format(expenseDate, 'yyyy-MM') === currentMonthValue;
    });
    return currentMonthExpenses.reduce((total, exp) => total + (parseFloat(exp.amount) || 0), 0).toFixed(2);
  }, [expenses, currentMonthValue]);

  const totalSpentValue = totalSpent();
  const effectiveBudget = monthlyBudgets[currentMonthValue] || null;
  const remainingBudget = effectiveBudget ? (effectiveBudget - totalSpentValue).toFixed(2) : null;
  const budgetPercentage = effectiveBudget ? Math.min((totalSpentValue / effectiveBudget) * 100, 100) : 0;

  const getProgressColor = useCallback(() => {
    if (!effectiveBudget) return 'success';
    if (budgetPercentage < 50) return 'success';
    if (budgetPercentage < 75) return 'warning';
    return 'error';
  }, [effectiveBudget, budgetPercentage]);

  const handleSetBudget = async () => {
    if (!checkAuth()) return;
    if (budgetInput.trim() === '' || isNaN(budgetInput) || !selectedMonth) {
      setError('Inserisci un importo valido e seleziona un mese.');
      enqueueSnackbar('Inserisci un importo valido e seleziona un mese.', { variant: 'error' });
      return;
    }
    const budgetAmount = parseFloat(budgetInput);
    const userId = user?.uid || auth.currentUser?.uid;
    if (!userId) {
      setError('Utente non autenticato.');
      return;
    }
    const budgetRef = doc(db, 'budgets', `${userId}_${selectedMonth}`);
    try {
      await setDoc(
        budgetRef,
        { userId, month: selectedMonth, budgetAmount },
        { merge: true }
      );
      setMonthlyBudgets((prev) => ({ ...prev, [selectedMonth]: budgetAmount }));
      setBudgetInput('');
      setSelectedMonth('');
      setError('');
      setEditBudgetOpen(false);
    } catch (err) {
      console.error('Errore nel impostare il budget:', err);
      setError('Errore nel impostare il budget.');
      enqueueSnackbar('Errore nel impostare il budget.', { variant: 'error' });
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!checkAuth()) return;
    try {
      const confirmed = window.confirm('Sei sicuro di voler eliminare questa spesa?');
      if (!confirmed) return;
      await deleteDoc(doc(db, 'expenses', id));
      enqueueSnackbar('Spesa eliminata con successo', { variant: 'success' });
    } catch (delErr) {
      console.error("Errore durante l'eliminazione:", delErr);
      enqueueSnackbar("Errore durante l'eliminazione della spesa", { variant: 'error' });
    }
  };

  const groupedExpenses = useMemo(() => getGroupedExpenses(), [getGroupedExpenses]);

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh" className={modeClass}>
      <Box component="main" sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Box sx={{ ...containerSx, pb: 8 }}>
          {!user && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: 'warning.light' }}>
              <Typography>Verifica autenticazione in corso...</Typography>
            </Paper>
          )}

          <Box
            sx={{
              position: 'fixed',
              top: { xs: '8px', sm: '16px' },
              right: { xs: '8px', sm: '16px' },
              zIndex: 1200,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<HomeIcon sx={{ color: 'text.primary' }} />}
              onClick={() => navigate('/dashboard')}
              sx={{
                borderRadius: '12px',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                px: { xs: 1, sm: 2 },
                fontSize: { xs: '0.8rem', sm: 'inherit' },
              }}
            >
              Dashboard
            </Button>
          </Box>

          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', pt: 2 }}>
            Le tue spese
          </Typography>
          {error && (
            <Typography variant="body1" color="error" gutterBottom>
              {error}
            </Typography>
          )}

          {loading ? (
            <Box sx={{ width: '100%' }}>
              <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 2 }} />
              <Skeleton variant="rectangular" height={150} sx={{ mb: 2, borderRadius: 2 }} />
              <Skeleton variant="rectangular" height={150} sx={{ mb: 2, borderRadius: 2 }} />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="category-select-label" sx={{ color: 'text.primary' }}>
                      Categoria
                    </InputLabel>
                    <Select
                      labelId="category-select-label"
                      value={selectedCategory}
                      label="Categoria"
                      onChange={handleCategoryChange}
                      sx={{ borderRadius: '8px', backgroundColor: 'background.paper', color: 'text.primary' }}
                    >
                      <MenuItem value="">
                        <em>Tutte le categorie</em>
                      </MenuItem>
                      {categories.map(cat => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="search-name-label" sx={{ color: 'text.primary' }}>
                      Cerca per nome
                    </InputLabel>
                    <OutlinedInput
                      label="Cerca per nome"
                      value={searchName}
                      onChange={handleSearchNameChange}
                      sx={{ borderRadius: '8px', backgroundColor: 'background.paper', color: 'text.primary' }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Paper
                sx={{
                  padding: 2,
                  mb: 3,
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                    Budget Mensile
                  </Typography>
                  <Tooltip title="Imposta Budget Mensile">
                    <IconButton
                      onClick={() => {
                        setBudgetInput('');
                        setSelectedMonth('');
                        setEditBudgetOpen(true);
                      }}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                        borderRadius: 0,
                        backgroundColor: 'transparent',
                      }}
                    >
                      <SettingsIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                {budgetLoading ? (
                  <CircularProgress size={24} />
                ) : effectiveBudget ? (
                  <>
                    <Typography variant="h5" sx={{ color: remainingBudget < 0 ? 'error.main' : 'text.primary', fontWeight: 'bold', mb: 1 }}>
                      €{remainingBudget} rimanenti
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={budgetPercentage}
                      color={getProgressColor()}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.primary' }}>
                      {budgetPercentage.toFixed(0)}% del budget utilizzato
                    </Typography>
                  </>
                ) : (
                  null
                )}
              </Paper>

              <Dialog
                open={editBudgetOpen}
                onClose={() => setEditBudgetOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                  sx: {
                    border: '1px solid',
                    borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                    borderRadius: 0,
                    backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#000000',
                  },
                }}
              >
                <DialogTitle sx={{ color: 'text.primary' }}>Imposta Budget Mensile</DialogTitle>
                <DialogContent>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      select
                      label="Mese"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderRadius: '8px',
                        backgroundColor: 'background.paper',
                        color: 'text.primary',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          color: 'text.primary',
                          '& fieldset': { borderColor: theme.palette.mode === 'light' ? '#000' : '#fff' },
                        },
                        '& .MuiInputLabel-root': { color: 'text.primary' },
                      }}
                    >
                      {Array.from({ length: 12 }, (_, i) => {
                        const date = new Date(currentYear, i, 1);
                        const monthName = format(date, 'MMMM', { locale: it });
                        const monthValue = format(date, 'yyyy-MM');
                        return (
                          <MenuItem key={monthValue} value={monthValue}>
                            {monthName} {currentYear}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="budget-input-dialog-label" sx={{ color: 'text.primary' }}>
                      Budget (€)
                    </InputLabel>
                    <OutlinedInput
                      label="Budget (€)"
                      type="number"
                      fullWidth
                      value={budgetInput}
                      onChange={(e) => setBudgetInput(e.target.value)}
                      sx={{
                        borderRadius: '8px',
                        backgroundColor: 'background.paper',
                        color: 'text.primary',
                        '& fieldset': { borderColor: theme.palette.mode === 'light' ? '#000' : '#fff' },
                      }}
                      inputProps={{ style: { color: theme.palette.text.primary } }}
                    />
                  </FormControl>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => setEditBudgetOpen(false)}
                    sx={{
                      border: '1px solid',
                      borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                      borderRadius: 0,
                      color: 'text.primary',
                      backgroundColor: 'transparent',
                      ':hover': { backgroundColor: theme.palette.mode === 'light' ? '#F0F0F0' : '#111111' },
                    }}
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={handleSetBudget}
                    sx={{
                      border: '1px solid',
                      borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                      borderRadius: 0,
                      color: 'text.primary',
                      backgroundColor: 'transparent',
                      ':hover': { backgroundColor: theme.palette.mode === 'light' ? '#F0F0F0' : '#111111' },
                    }}
                  >
                    Salva
                  </Button>
                </DialogActions>
              </Dialog>

              <Paper
                sx={{
                  padding: 2,
                  mb: 3,
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1, color: 'text.primary' }}>
                  Totale Spese Mese Corrente: €{totalSpentValue}
                </Typography>
                {effectiveBudget && (
                  <Typography variant="h6" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                    Budget Rimanente: €{remainingBudget}
                  </Typography>
                )}
              </Paper>

              {Object.keys(getGroupedExpenses())
                .sort((a, b) => b - a)
                .map(year => (
                  <Accordion
                    key={year}
                    defaultExpanded={false}
                    TransitionProps={{ unmountOnExit: true }}
                    sx={{
                      borderRadius: 0,
                      border: '1px solid',
                      borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                      backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#000000',
                      mb: 2,
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.primary' }} />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box className={`pallino ${getPallinoClass(year, 'year', colorSets)}`} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', flexGrow: 1, ml: 1 }}>
                          {year}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                          Totale Anno: €{getGroupedExpenses()[year]?.total?.toFixed(2) || '0.00'}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#000000',
                        borderTop: '1px solid',
                        borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                      }}
                    >
                      {Object.keys(getGroupedExpenses()[year].months)
                        .sort((a, b) => new Date(`${b} 1, ${year}`) - new Date(`${a} 1, ${year}`))
                        .map(month => (
                          <Accordion
                            key={month}
                            defaultExpanded
                            TransitionProps={{ unmountOnExit: true }}
                            sx={{
                              border: '1px solid',
                              borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                              borderRadius: 0,
                              backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#000000',
                              mb: 1,
                              '&:before': { display: 'none' },
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.primary' }} />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Box className={`pallino ${getPallinoClass(month, 'month', colorSets)}`} />
                                <Typography variant="subtitle1" sx={{ color: 'text.primary', flexGrow: 1, ml: 1 }}>
                                  {month}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
                                  Totale Mese: €{getGroupedExpenses()[year].months[month]?.total?.toFixed(2) || '0.00'}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#000000',
                                borderTop: '1px solid',
                                borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                              }}
                            >
                              {Object.keys(getGroupedExpenses()[year].months[month].categories)
                                .sort()
                                .map(cat => (
                                  <Accordion
                                    key={cat}
                                    defaultExpanded={false}
                                    TransitionProps={{ unmountOnExit: true }}
                                    sx={{
                                      border: '1px solid',
                                      borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                                      borderRadius: 0,
                                      backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#000000',
                                      mb: 1,
                                      '&:before': { display: 'none' },
                                    }}
                                  >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.primary' }} />}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Box className={`pallino ${getPallinoClass(cat, 'category', colorSets)}`} />
                                        <Tooltip title={cat} TransitionComponent={Zoom} enterDelay={500} leaveDelay={200}>
                                          <Typography variant="subtitle2" className="category-name" sx={{ color: 'text.primary', flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ml: 1 }}>
                                            {cat}
                                          </Typography>
                                        </Tooltip>
                                        <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                          €{getGroupedExpenses()[year].months[month].categories[cat]?.total?.toFixed(2) || '0.00'}
                                        </Typography>
                                      </Box>
                                    </AccordionSummary>
                                    <AccordionDetails
                                      sx={{
                                        backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#000000',
                                        borderTop: '1px solid',
                                        borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                                      }}
                                    >
                                      {getGroupedExpenses()[year].months[month].categories[cat].transactions.map(expense => (
                                        <Paper
                                          key={expense.id}
                                          className="expense-paper"
                                          elevation={0}
                                          sx={{ ...styles.cardAnimation }}
                                        >
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                              {expense.name || 'Non specificato'}
                                            </Typography>
                                            <Chip label={`€${parseFloat(expense.amount)?.toFixed(2) || '0.00'}`} className="expense-chip" />
                                          </Box>
                                          <Typography variant="body2" className="expense-date">
                                            Data:{' '}
                                            {(() => {
                                              const parsedDate = safeParseDate(expense.date);
                                              return parsedDate
                                                ? format(parsedDate, 'dd MMMM yyyy', { locale: it })
                                                : 'Data non disponibile';
                                            })()}
                                          </Typography>
                                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <IconButton onClick={() => handleDeleteExpense(expense.id)} className="icon-button-hover">
                                              <DeleteIcon />
                                            </IconButton>
                                          </Box>
                                        </Paper>
                                      ))}
                                    </AccordionDetails>
                                  </Accordion>
                                ))}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              {expenses.length === 0 && !error && (
                <Paper
                  sx={{
                    padding: 2,
                    borderRadius: 0,
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
                    textAlign: 'center',
                    backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#000000',
                    color: 'text.primary',
                  }}
                >
                  <Typography variant="body1">Non hai ancora aggiunto spese.</Typography>
                </Paper>
              )}
            </>
          )}
        </Box>
      </Box>

      <Dialog
        open={addExpenseOpen}
        onClose={() => setAddExpenseOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: '1px solid',
            borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
            backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#000000',
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>Aggiungi Spesa</DialogTitle>
        <DialogContent>
          <AddExpense onClose={() => setAddExpenseOpen(false)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddExpenseOpen(false)} sx={{ cursor: 'pointer' }}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
        }}
      >
        <BottomNavigation
          value={bottomNavValue}
          onChange={(event, newValue) => setBottomNavValue(newValue)}
          showLabels
          sx={{
            backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
            borderTop: '1px solid',
            borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
            boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.2)',
            width: '100%',
          }}
        >
          <BottomNavigationAction
            label="Dashboard"
            value="dashboard"
            icon={<DashboardIcon sx={{ color: 'text.primary' }} />}
            onClick={() => navigate('/dashboard')}
            sx={{ color: 'text.primary', '&.Mui-selected': { color: 'text.primary' } }}
          />
          <BottomNavigationAction
            label="Statistiche"
            value="statistics"
            icon={<BarChartIcon sx={{ color: 'text.primary' }} />}
            onClick={() => navigate('/statistics')}
            sx={{ color: 'text.primary', '&.Mui-selected': { color: 'text.primary' } }}
          />
          <BottomNavigationAction
            label="Spese"
            value="expense-list"
            icon={<ListAltIcon sx={{ color: 'text.primary' }} />}
            onClick={() => navigate('/expense-list', { state: { userFamily } })}
            sx={{ color: 'text.primary', '&.Mui-selected': { color: 'text.primary' } }}
          />
          <BottomNavigationAction
            label="Aggiungi"
            value="add-expense"
            icon={<AddIcon sx={{ color: 'text.primary' }} />}
            onClick={() => setAddExpenseOpen(true)}
            sx={{ color: 'text.primary', '&.Mui-selected': { color: 'text.primary' } }}
          />
        </BottomNavigation>
      </Box>
    </Box>
  );
};

export default ExpenseList;
